/**
 * Which turns already had their one automatic fix stream after a failed resume.
 *
 * Stored in `localStorage` (not `sessionStorage` or a ref) so the record survives a reload and
 * is shared by every tab: reopening a conversation whose last build failed must not start a
 * paid fix stream again each time. Storage can be missing (private mode, node tests) or throw;
 * every access is guarded, and a missing store only means "not attempted".
 *
 * Phase 6 of PROTOTYPER_STABILITY_TECH_PLAN.md moves this counter onto the server-side version
 * record; this keeps the interim behavior bounded until then.
 */

const KEY_PREFIX = 'ideoz:prototyper:resume-fix-attempted:';
/** Newest turns kept per conversation; older entries are dropped when the list grows. */
const MAX_TURNS_PER_CONVERSATION = 50;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function read(conversationId: string): string[] {
  const store = storage();
  if (!store) return [];
  try {
    const raw = store.getItem(KEY_PREFIX + conversationId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function hasAttemptedResumeFix(
  conversationId: string | undefined,
  llmInteractionId: string
): boolean {
  if (!conversationId) return false;
  return read(conversationId).includes(llmInteractionId);
}

export function markResumeFixAttempted(
  conversationId: string | undefined,
  llmInteractionId: string
): void {
  if (!conversationId) return;
  const store = storage();
  if (!store) return;
  const ids = read(conversationId).filter((id) => id !== llmInteractionId);
  ids.push(llmInteractionId);
  try {
    store.setItem(
      KEY_PREFIX + conversationId,
      JSON.stringify(ids.slice(-MAX_TURNS_PER_CONVERSATION))
    );
  } catch {
    // Quota or blocked storage: the attempt still ran; it just isn't remembered.
  }
}
